import React, { useState, useRef, useCallback } from "react";
import { ContentHeader } from "@components";
import DataTable from "../components/datatable/DataTable";
import TestTypeModal from "../components/modal/TestTypeModal";
import ViewTestTypeModal from "../components/modal/ViewTestTypeModal";
import API from "../api";
import toastr from "toastr";

interface TestType {
  id: number;
  test_code: string;
  test_name: string;
  price: number;
  lower_limit?: string;
  upper_limit?: string;
  units?: string;
  type_test: string;
  sub_head?: string;
  sample_type?: string;
  table_input?: string;
  critical_info?: string;
  interpretation?: string;
  test_category?: number;
  category_name?: string;
  remarks?: string;
  method?: string;
  comments?: string;
  show_critical_info?: number;
  show_interpretation?: number;
}

const TestTypes: React.FC = () => {
  const [showModal, setShowModal] = useState(false);
  const [selectedTestType, setSelectedTestType] = useState<TestType | null>(null);
  const [modalMode, setModalMode] = useState<"add" | "edit">("add");
  const [showImportModal, setShowImportModal] = useState(false);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importing, setImporting] = useState(false);

  const [showViewModal, setShowViewModal] = useState(false);
  const [viewId, setViewId] = useState<number | null>(null);

  const tableRef = useRef<any>(null);

  const fetchTestTypes = useCallback(
    async (
      page: number,
      search: string,
      sortBy = "test_name",
      sortOrder = "asc"
    ) => {
      try {
        const res = await API.get(
          `testTypes/getTestTypes?page=${page}&pageSize=25&search=${encodeURIComponent(
            search
          )}&sortBy=${sortBy}&sortOrder=${sortOrder}`
        );
        return { data: res.data.data, total: res.data.total };
      } catch {
        toastr.error("Failed to fetch test types");
        return { data: [], total: 0 };
      }
    },
    []
  );

  const handleAdd = useCallback(() => {
    setModalMode("add");
    setSelectedTestType(null);
    setShowModal(true);
  }, []);

  const handleEdit = useCallback((testType: TestType) => {
    setModalMode("edit");
    setSelectedTestType(testType);
    setShowModal(true);
  }, []);

  const handleDelete = useCallback(async (id: number) => {
    if (!window.confirm("Are you sure you want to delete this test type?"))
      return;
    try {
      await API.delete(`testTypes/deleteTestType/${id}`);
      toastr.success("Test type deleted successfully");
      tableRef.current?.refetch();
    } catch {
      toastr.error("Failed to delete test type");
    }
  }, []);

  // Now opens the View modal instead of alert
  const handleView = useCallback((id: number) => {
    setViewId(id);
    setShowViewModal(true);
  }, []);

  const handleImport = async () => {
    if (!importFile) {
      toastr.error("Please select a file");
      return;
    }
    const formData = new FormData();
    formData.append("file", importFile);
    setImporting(true);
    try {
      await API.post("testTypes/importTestTypes", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toastr.success("Test data imported successfully");
      setShowImportModal(false);
      setImportFile(null);
      tableRef.current?.refetch();
    } catch (err: any) {
      const msg = err.response?.data?.message || "Failed to import test data";
      toastr.error(msg);
    } finally {
      setImporting(false);
    }
  };

  const handleModalClose = useCallback(() => {
    setShowModal(false);
    setSelectedTestType(null);
  }, []);

  const handleCloseImport = () => {
    if (!importing) {
      setShowImportModal(false);
      setImportFile(null);
    }
  };

  const columns = [
    {
      header: "Dept",
      accessorKey: "category_name",
      cell: ({ row }: any) => row.original.category_name || "-",
    },
    {
      header: "Test Code",
      accessorKey: "test_code",
      cell: ({ row }: any) => row.original.test_code || "-",
    },
    {
      header: "Test Name",
      accessorKey: "test_name",
    },
    {
      header: "Price",
      accessorKey: "price",
      cell: ({ row }: any) => `₹${row.original.price ?? 0}`,
    },
    {
      header: "Lower Limit",
      accessorKey: "lower_limit",
      cell: ({ row }: any) => row.original.lower_limit || "-",
    },
    {
      header: "Upper Limit",
      accessorKey: "upper_limit",
      cell: ({ row }: any) => row.original.upper_limit || "-",
    },
    {
      header: "Action",
      id: "actions",
      cell: ({ row }: any) => {
        const data: TestType = row.original;
        return (
          <div className="d-flex justify-content-center gap-1">
            <button
              className="btn btn-light"
              onClick={() => handleView(data.id)}
              title="View"
            >
              <i className="fa fa-eye" />
            </button>
            <button
              className="btn btn-light"
              onClick={() => handleEdit(data)}
              title="Edit"
            >
              <i className="fa fa-edit" />
            </button>
            <button
              className="btn btn-light"
              onClick={() => handleDelete(data.id)}
              title="Delete"
            >
              <i className="fa fa-trash" />
            </button>
          </div>
        );
      },
    },
  ];

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <ContentHeader title="Test" />
        <div className="d-flex gap-2">
          <button className="btn btn-light" onClick={handleAdd}>
            <i className="fa fa-plus me-1" /> Create New
          </button>
        </div>
      </div>

      <section className="content">
        <div className="container-fluid">
          <div className="card card-outline card-primary">
            <div className="card-body">
              <DataTable
                ref={tableRef}
                columns={columns}
                queryFn={fetchTestTypes}
                queryKey={["testTypes"]}
              />
            </div>
          </div>
        </div>
      </section>

      {/* View Modal */}
      <ViewTestTypeModal
        show={showViewModal}
        onClose={() => {
          setShowViewModal(false);
          setViewId(null);
        }}
        testId={viewId}
      />

      {/* Add/Edit Modal */}
      <TestTypeModal
        show={showModal}
        onClose={handleModalClose}
        mode={modalMode}
        testTypeData={selectedTestType}
        onSuccess={() => {
          handleModalClose();
          tableRef.current?.refetch();
        }}
      />

      {/* Import Modal */}
      {showImportModal && (
        <div
          className="modal show d-block"
          style={{ background: "rgba(0,0,0,0.5)", zIndex: 1050 }}
          onClick={(e) => {
            if (e.target === e.currentTarget) handleCloseImport();
          }}
        >
          <div
            className="modal-dialog"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-content bg-dark text-white">
              <div className="modal-header border-secondary">
                <h5 className="modal-title">Import Test Data</h5>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={handleCloseImport}
                  disabled={importing}
                />
              </div>
              <div className="modal-body">
                <p className="text-muted small mb-2">
                  Upload an Excel (.xlsx) or CSV file to import test data.
                </p>
                <input
                  type="file"
                  accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                  onChange={(e) => setImportFile(e.target.files?.[0] || null)}
                  className="form-control bg-dark text-white border-secondary"
                />
                {importFile && (
                  <p className="text-success small mt-2 mb-0">
                    Selected: {importFile.name}
                  </p>
                )}
              </div>
              <div className="modal-footer border-secondary">
                <button
                  className="btn btn-light"
                  onClick={handleImport}
                  disabled={!importFile || importing}
                >
                  {importing ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-1"
                        role="status"
                      />
                      Importing...
                    </>
                  ) : (
                    <>
                      <i className="fa fa-upload me-1" /> Import
                    </>
                  )}
                </button>
                <button
                  className="btn btn-light"
                  onClick={handleCloseImport}
                  disabled={importing}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TestTypes;